import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import { Avatar } from './Avatar';
import { Badge } from './Badge';
import { UserAvatar } from './types';
import { SimilarPromptsModal, SimilarPrompt } from '../SimilarPromptsModal';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '16px',
    },
    info: {
      flex: 1,
    },
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '8px',
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      margin: 0,
      color: theme.palette.text.primary,
    },
    users: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    usersText: {
      fontSize: '13px',
      color: theme.palette.text.secondary,
    },
    avatarGroup: {
      display: 'flex',
      gap: '4px',
    },
    copyButton: {
      padding: '8px',
    },
  }),
);

export interface PromptItemHeaderProps {
  title: string;
  similarCount: number;
  usedByCount: number;
  users: UserAvatar[];
  onCopy?: () => void;
  className?: string;
}

// Helper function to generate similar prompts data
const generateSimilarPrompts = (baseTitle: string, count: number): SimilarPrompt[] => {
  const domains = ['CloudAssistant', 'EllisAIForResearch', 'AutoDomain_2025-11-06T14:3933', 'SmartLeaseAI'];
  const subDomains = ['Submissions', 'Default', 'EllisAIForResearch', 'AutoSubDomain_2025-11-06T143933', '---'];
  const personas = ['Submissions', 'Default', 'AutoPersona_2025-1-06T14:3933', '---'];
  const users = ['John Smith', 'Sarah Johnson', 'Jane Doe', 'John Doe'];
  
  // Prompt variation prefixes/suffixes for more realism
  const variations = [
    '',
    'Please ',
    'Can you ',
    'Help me ',
    'I need to ',
    'Could you ',
  ];
  
  const suffixes = [
    '',
    ' for my project',
    ' with validation',
    ' step by step',
    ' in detail',
    ' with examples',
  ];
  
  const similarPrompts: SimilarPrompt[] = [];
  
  for (let i = 0; i < Math.min(count, 20); i++) {
    const randomDomain = domains[Math.floor(Math.random() * domains.length)];
    const randomSubDomain = subDomains[Math.floor(Math.random() * subDomains.length)];
    const randomPersona = personas[Math.floor(Math.random() * personas.length)];
    const randomUser = users[Math.floor(Math.random() * users.length)];
    const randomPrefix = variations[Math.floor(Math.random() * variations.length)];
    const randomSuffix = suffixes[Math.floor(Math.random() * suffixes.length)];
    
    // Generate varied dates between June 2024 and current date
    const startDate = new Date(2024, 5, 7); // June 7, 2024
    const endDate = new Date(2025, 11, 5); // Dec 5, 2025
    const randomTimestamp = startDate.getTime() + Math.random() * (endDate.getTime() - startDate.getTime());
    const randomDate = new Date(randomTimestamp);
    const formattedDate = `${String(randomDate.getMonth() + 1).padStart(2, '0')}/${String(randomDate.getDate()).padStart(2, '0')}/${randomDate.getFullYear()}`;
    
    // Create variation of the prompt
    const promptText = i === 0 ? baseTitle : `${randomPrefix}${baseTitle.toLowerCase()}${randomSuffix}`;
    
    similarPrompts.push({
      id: `similar-${i}`,
      prompt: promptText,
      domain: randomDomain,
      subDomain: randomSubDomain,
      persona: randomPersona,
      createdBy: randomUser,
      createdDate: formattedDate,
    });
  }
  
  return similarPrompts;
};

export const PromptItemHeader: React.FC<PromptItemHeaderProps> = ({
  title,
  similarCount,
  usedByCount,
  users,
  onCopy,
  className = '',
}) => {
  const classes = useStyles();
  const [modalOpen, setModalOpen] = useState(false);
  const [similarPrompts, setSimilarPrompts] = useState<SimilarPrompt[]>([]);
  
  // Show up to 5 avatars, with a +N indicator if there are more
  const displayUsers = users.slice(0, 5);
  const remainingCount = users.length - 5;

  const handleBadgeClick = () => {
    // Generate similar prompts when badge is clicked
    const prompts = generateSimilarPrompts(title, similarCount);
    setSimilarPrompts(prompts);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  return (
    <>
      <Box className={`${classes.container} ${className}`}>
        <Box className={classes.info}>
          <Box className={classes.titleContainer}>
            <Typography className={classes.title}>{title}</Typography>
            <Badge count={similarCount} label="similar prompts" onClick={handleBadgeClick} />
          </Box>
          
          <Box className={classes.users}>
            <Typography className={classes.usersText}>
              Used by {usedByCount} different users:
            </Typography>
            <Box className={classes.avatarGroup}>
              {displayUsers.map((user) => (
                <Avatar key={user.id} initials={user.initials} size="extra-small" />
              ))}
              {remainingCount > 0 && (
                <Avatar initials={`+${remainingCount}`} size="extra-small" />
              )}
            </Box>
          </Box>
        </Box>

        {onCopy && (
          <IconButton
            className={classes.copyButton}
            onClick={onCopy}
            aria-label="Copy prompt"
            size="small"
          >
            <FileCopyOutlinedIcon />
          </IconButton>
        )}
      </Box>
      
      <SimilarPromptsModal
        open={modalOpen}
        onClose={handleModalClose}
        prompts={similarPrompts}
        title="Similar Prompts"
        breadcrumb="Data Dashboards > Similar Prompts"
      />
    </>
  );
};
